import { useEffect } from 'react';
import { Modal, Form, Input, InputNumber, Switch, App } from 'antd';
import { useUpdateInvestor } from '@/hooks/useInvestors';
import type { Investor } from '@/api/investors';

interface Props {
  open: boolean;
  investor: Investor | null;
  onClose: () => void;
}

export function EditInvestorModal({ open, investor, onClose }: Props) {
  const { message } = App.useApp();
  const [form] = Form.useForm<{
    name: string;
    display_order: number;
    active: boolean;
    notes?: string;
  }>();
  const mut = useUpdateInvestor();

  useEffect(() => {
    // Only touch the form while the modal is open — with destroyOnHidden the
    // <Form> is unmounted when closed, so setFieldsValue would warn about an
    // unconnected form instance.
    if (open && investor)
      form.setFieldsValue({
        name: investor.name,
        display_order: investor.display_order,
        active: investor.active,
        notes: investor.notes ?? '',
      });
  }, [open, investor, form]);

  async function submit() {
    if (!investor) return;
    const v = await form.validateFields();
    try {
      await mut.mutateAsync({
        id: investor.id,
        input: {
          name: v.name.trim(),
          display_order: v.display_order,
          active: v.active,
          notes: v.notes?.trim() || null,
        },
      });
      message.success('Saved');
      onClose();
    } catch (e) {
      message.error((e as Error).message);
    }
  }

  return (
    <Modal
      open={open}
      title={investor ? `Edit ${investor.name}` : 'Edit'}
      onCancel={onClose}
      onOk={submit}
      confirmLoading={mut.isPending}
      destroyOnHidden
    >
      <Form form={form} layout="vertical">
        <Form.Item label="Name" name="name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item label="Display order" name="display_order">
          <InputNumber min={0} max={9999} />
        </Form.Item>
        <Form.Item label="Active" name="active" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item label="Notes" name="notes">
          <Input.TextArea rows={3} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
